import ServiceFactory from "utilities/services/service-factory";
import ChapterRecord from "models/view-models/chapter-record";
import ServiceHookFactory from "utilities/services/service-hook-factory";
import QueryHookFactory from "utilities/hooks/queries/query-hook-factory";

// ---------------------------------------------------------------------------------------------
// #region Interfaces
// ---------------------------------------------------------------------------------------------

export interface ChapterBasePathParams {
    publicationId: number;
}

export interface ChapterResourcePathParams extends ChapterBasePathParams {
    id: number;
}

/**
 * Search parameters when listing Chapter resources.
 * These get translated to QueryString parameters
 */
export interface ChapterListQueryParams {
    nfpaLabel?: string;
}

// #endregion Interfaces

// ---------------------------------------------------------------------------------------------
// #region Constants
// ---------------------------------------------------------------------------------------------

const baseEndpoint = "publications/:publicationId/chapters";
const resourceType = ChapterRecord;
const resourceEndpoint = `${baseEndpoint}/:id`;
const key = "publications-chapter";

// #endregion Constants

// ---------------------------------------------------------------------------------------------
// #region Service
// ---------------------------------------------------------------------------------------------

const ChapterService = {
    /**
     * Retrieve a specific Chapter resource
     */
    get: ServiceFactory.get<ChapterRecord, ChapterResourcePathParams>(
        resourceType,
        resourceEndpoint
    ),

    /**
     * Retrieves a list of Chapter resources based upon supplied search criteria
     */
    list: ServiceFactory.nestedList<
        ChapterRecord,
        ChapterListQueryParams,
        ChapterBasePathParams
    >(resourceType, baseEndpoint),

    /**
     * Custom hook for leveraging service get calls in react components
     */
    useGet: ServiceHookFactory.useGet<ChapterRecord, ChapterResourcePathParams>(
        resourceType,
        resourceEndpoint
    ),
    useGetQuery: QueryHookFactory.useGetQuery<
        ChapterRecord,
        ChapterResourcePathParams
    >(key, resourceType, resourceEndpoint),

    /**
     * Custom hook for leveraging service list calls in react components
     */
    useList: ServiceHookFactory.useNestedList<
        ChapterRecord,
        ChapterBasePathParams,
        ChapterListQueryParams
    >(resourceType, baseEndpoint),
    useListQuery: QueryHookFactory.useNestedListQuery<
        ChapterRecord,
        ChapterBasePathParams,
        ChapterListQueryParams
    >(key, resourceType, baseEndpoint),
};

// #endregion Service

// ---------------------------------------------------------------------------------------------
// #region Exports
// ---------------------------------------------------------------------------------------------

export default ChapterService;

// #endregion Exports
